import type { DependencyList } from 'react';

import isEqual from 'fast-deep-equal/react';

import { useCompareMemo } from './compare-memo.hook';

/**
 * A hook that memoizes a value using deep comparison.
 *
 * @param factory - A function that returns the value to be memoized.
 * @param dependencies - An array of dependencies that, when changed, will cause the value to be recomputed.
 * @returns The memoized value.
 *
 * @example
 * const memoizedValue = useDeepCompareMemo(() => computeExpensiveValue(a, b), [a, b]);
 */
export const useDeepCompareMemo = <TData = unknown>(factory: () => TData, dependencies: DependencyList) => {
  // biome-ignore lint/correctness/useExhaustiveDependencies: dependencies are memoized using useCompareMemo
  return useCompareMemo(factory, dependencies, isEqual);
};
